import mixpanel from 'mixpanel-browser';

const MIXPANEL_TOKEN =
  (import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined) ||
  (typeof process !== 'undefined' ? process.env.VITE_MIXPANEL_TOKEN : undefined);

type AnalyticsEventMap = {
  signup_started: { referral_source: string; platform: 'Web' | 'Customer-facing web' };
  signup_completed: { tester_id: string; plan: string; platform: 'Web' | 'Customer-facing web' };
  onboarding_step_viewed: { tester_id: string; step_name: string; step_number: number };
  onboarding_step_completed: { tester_id: string; step_name: string; step_number: number };
  onboarding_abandoned: { tester_id: string; last_step_viewed: string };
  profile_setup_completed: { tester_id: string; services_offered: number; service_area: string };
  estimate_viewed: { estimate_id: string; viewed_at: string };
  estimate_accepted: { estimate_id: string; time_to_accept: number };
  estimate_declined: { estimate_id: string; reason: string };
  estimate_changes_requested: { estimate_id: string };
  invoice_viewed: { invoice_id: string; viewed_at: string };
  invoice_paid: { invoice_id: string; time_to_pay: number; payment_method: string };
  invoice_partial_payment: { invoice_id: string; amount_paid: number; balance_remaining: number };
  payment_collected_online: { invoice_id: string; amount: number; method: string };
  error_encountered: { screen_name: string; error_type: string; error_message: string };
};

type AnalyticsEventName = keyof AnalyticsEventMap;

const sanitizeErrorMessage = (message: string): string =>
  message
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[redacted-email]')
    .replace(/Bearer\s+[^\s;]+/gi, '[redacted-token]')
    .replace(/\b(?:access|refresh|id)[_-]?token\s*[:=]\s*[^\s;]+/gi, '[redacted-token]')
    .slice(0, 160);

export const elapsedSecondsSince = (
  timestamp: string | null | undefined,
  now = Date.now()
): number => {
  if (!timestamp) return 0;
  const startedAt = Date.parse(timestamp);
  if (!Number.isFinite(startedAt)) return 0;
  return Math.max(0, Math.round((now - startedAt) / 1000));
};

export class Analytics {
  constructor(private readonly token: string | undefined = MIXPANEL_TOKEN) {}

  private initialized = false;
  private unavailable = false;
  private trackedViews = new Set<string>();
  private consentState: 'unknown' | 'granted' | 'denied' = 'unknown';
  private pendingEvents: Array<{
    event: AnalyticsEventName;
    properties: Record<string, string | number | boolean>;
  }> = [];

  initialize(): boolean {
    if (this.initialized) return true;
    if (this.unavailable || !this.token) {
      this.unavailable = true;
      return false;
    }
    mixpanel.init(this.token, {
      opt_out_tracking_by_default: true,
      opt_out_persistence_by_default: true,
      track_pageview: false,
      autocapture: false,
      persistence: 'localStorage',
    });
    this.initialized = true;
    return true;
  }
  applyConsent(consented: boolean | null | undefined, userId?: string) {
    if (!this.initialize()) return;
    if (consented === true) {
      this.consentState = 'granted';
      if (userId) mixpanel.identify(userId);
      mixpanel.opt_in_tracking();
      for (const pending of this.pendingEvents) {
        mixpanel.track(pending.event, pending.properties, { send_immediately: true });
      }
      this.pendingEvents = [];
      return;
    }
    this.consentState = consented === false ? 'denied' : 'unknown';
    if (consented === false) {
      this.pendingEvents = [];
      this.trackedViews.clear();
    }
    mixpanel.opt_out_tracking();
    mixpanel.reset();
  }
  trackEvent<EventName extends AnalyticsEventName>(
    event: EventName,
    properties: AnalyticsEventMap[EventName]
  ) {
    if (!this.initialize()) return;
    if (this.consentState === 'denied') return;
    if (this.consentState === 'unknown') {
      if (this.pendingEvents.length >= 100) this.pendingEvents.shift();
      this.pendingEvents.push({
        event,
        properties: properties as Record<string, string | number | boolean>,
      });
      return;
    }
    mixpanel.track(event, properties);
  }
  trackViewOnce<EventName extends AnalyticsEventName>(
    event: EventName,
    viewKey: string,
    properties: AnalyticsEventMap[EventName]
  ) {
    if (this.consentState === 'denied' || this.trackedViews.has(viewKey)) return;
    this.trackedViews.add(viewKey);
    this.trackEvent(event, properties);
  }
  trackError(screenName: string, errorType: string, message: string) {
    this.trackEvent('error_encountered', {
      screen_name: screenName,
      error_type: errorType,
      error_message: sanitizeErrorMessage(message),
    });
  }
  reset() {
    if (!this.initialize()) return;
    this.consentState = 'unknown';
    this.pendingEvents = [];
    this.trackedViews.clear();
    mixpanel.opt_out_tracking();
    mixpanel.reset();
  }
}
export const analytics = new Analytics();
